import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Hammer, MapPin, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import AnimatedSection from '../components/AnimatedSection';
import { houseModels, constructionProjects } from '../mockData';

const ModelsPage = () => {
  const [filter, setFilter] = useState('all');
  const models = houseModels;

  const filteredModels = filter === 'all' ? models : models.filter(m => m.type === filter);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div
        className="relative h-80 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=1600)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-white">
            <div className="flex items-center text-sm mb-4">
              <Link to="/" className="opacity-90 hover:opacity-100">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span>Modèles</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold">Nos modèles de maisons</h1>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-[#c4d600] hover:bg-[#b0c200] text-gray-800' : ''}
            >
              Tous les modèles
            </Button>
            <Button
              variant={filter === 'Plain-pied' ? 'default' : 'outline'}
              onClick={() => setFilter('Plain-pied')}
              className={filter === 'Plain-pied' ? 'bg-[#c4d600] hover:bg-[#b0c200] text-gray-800' : ''}
            >
              Plain-pied
            </Button>
            <Button
              variant={filter === 'Étage' ? 'default' : 'outline'}
              onClick={() => setFilter('Étage')}
              className={filter === 'Étage' ? 'bg-[#c4d600] hover:bg-[#b0c200] text-gray-800' : ''}
            >
              À étage
            </Button>
            <Button
              variant={filter === 'Demi-niveau' ? 'default' : 'outline'}
              onClick={() => setFilter('Demi-niveau')}
              className={filter === 'Demi-niveau' ? 'bg-[#c4d600] hover:bg-[#b0c200] text-gray-800' : ''}
            >
              Demi-niveau
            </Button>
          </div>
        </div>
      </div>

      {/* Models Grid */}
      <AnimatedSection animation="fade-up">
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredModels.map((model, index) => (
                <AnimatedSection 
                  key={model.id} 
                  animation="fade-up" 
                  delay={index * 100}
                  className="h-full"
                >
                  <Link
                    to={`/modele/${model.id}`}
                    className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 block h-full"
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={model.image}
                        alt={model.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/800x600?text=Image+non+disponible';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="p-6">
                      <p className="text-sm text-[#c4d600] font-semibold mb-1">Modèle {model.name}</p>
                      <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-[#c4d600] transition-colors">{model.title}</h3>
                      <p className="text-sm text-gray-600">{model.type} • {model.style}</p>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Construction Projects Section */}
      {constructionProjects && Array.isArray(constructionProjects) && constructionProjects.length > 0 && (
        <AnimatedSection animation="fade-up">
          <div className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <p className="text-[#c4d600] font-semibold mb-2">PROJETS EN COURS</p>
                <h2 className="text-4xl font-bold text-gray-800 mb-4">Projets de construction</h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  Découvrez nos projets de construction actuellement en cours dans les Alpes-de-Haute-Provence
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {constructionProjects.map((project, index) => (
                  project && (
                  <AnimatedSection 
                    key={project.id} 
                    animation="fade-up" 
                    delay={index * 100}
                    className="h-full"
                  >
                    <Link
                      to={`/projet/${project.id}`}
                      className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 block h-full"
                    >
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/800x600?text=Image+non+disponible';
                            e.target.onerror = null;
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute top-4 right-4 bg-[#c4d600] text-gray-800 px-3 py-1 rounded-full text-xs font-semibold">
                          {project.status}
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-[#c4d600] transition-colors">
                          {project.title}
                        </h3>
                        {project.subtitle && (
                          <p className="text-sm text-gray-600 mb-3">{project.subtitle}</p>
                        )}
                        <div className="flex items-center text-sm text-gray-600 mb-3">
                          <MapPin className="w-4 h-4 mr-2 text-[#c4d600]" />
                          <span>{project.commune || project.location}</span>
                        </div>
                        <div className="flex items-center text-sm text-[#c4d600] font-semibold">
                          <Building2 className="w-4 h-4 mr-2" />
                          <span>{project.projectType}</span>
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>
                  )
                ))}
              </div>
            </div>
          </div>
        </AnimatedSection>
      )}
    </div>
  );
};

export default ModelsPage;